import {
  useQuery,
  type QueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { useDebouncedValue } from '../../../use-debounced-value/use-debounced-value';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';
import type { ApiError } from '../../../../utils/api/ApiError/ApiError';
import {
  pageableShape,
  type PageableSortDirections,
} from '../use-get-users-by-orgId-with-pagination-api/use-get-users-by-orgId-with-pagination-api';
import { classResponseSchema, type Class } from '../use-get-classes-api';

const classesByOrgIdWithPaginationShape = z.object({
  content: classResponseSchema,
  ...pageableShape.shape,
});

export type CompositeClassesWithPagination = z.infer<
  typeof classesByOrgIdWithPaginationShape
>;

export interface GetClassesByOrgIdWithPaginationParams {
  userId: string;
  orgId: string;
  searchValue?: string;
}

export type PageableClassSortableFields =
  | 'name'
  | 'teacher'
  | 'grade'
  | 'students'
  | 'organization';
export type PageableClassSort = [
  PageableClassSortableFields,
  PageableSortDirections
];

type PaginationData = {
  page: number | string;
  size: number | string;
  sort: PageableClassSort;
};

export const defaultClassPaginationData: PaginationData = {
  page: 0,
  size: 10,
  sort: ['name', 'asc'],
};

export async function getClassesByOrgIdWithPaginationApi(
  { userId, orgId, searchValue }: GetClassesByOrgIdWithPaginationParams,
  paginationData?: PaginationData
): Promise<CompositeClassesWithPagination> {
  const { sort, ...basePaginationData } = {
    ...defaultClassPaginationData,
    ...paginationData,
  };

  const classes = await fetcher<CompositeClassesWithPagination>(
    `/composite/classes/orgs/${orgId}/users/${userId}/class-types/CLASS`,
    {
      service: '/roster',
      version: '/v3',
      queryParameters: [
        ['searchValue', searchValue],
        ['sort', sort.join(',')],
        ...Object.entries(basePaginationData),
      ] as string[][],
    }
  );

  return classesByOrgIdWithPaginationShape.parse(classes);
}

export const getClassesByOrgIdWithPaginationApiQueryKey = (
  args?: Partial<GetClassesByOrgIdWithPaginationParams> &
    Partial<PaginationData>
) => {
  const { userId, orgId, page, size, sort, searchValue } = args ?? {};

  return [
    '/v3',
    'classes',
    userId,
    orgId,
    page,
    size,
    sort,
    searchValue,
  ].filter(Boolean) as string[];
};

export function useGetClassesByOrgIdWithPaginationApi(
  args: Partial<GetClassesByOrgIdWithPaginationParams>,
  paginationData: PaginationData = defaultClassPaginationData,
  customQueryOptions?: QueryOptions<CompositeClassesWithPagination, ApiError>
): UseQueryResult<CompositeClassesWithPagination, ApiError> {
  const { userId, orgId, searchValue } = args;
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 200);

  return useQuery<CompositeClassesWithPagination, ApiError>({
    queryKey: getClassesByOrgIdWithPaginationApiQueryKey({
      userId,
      orgId,
      searchValue: debouncedSearchValue,
      ...paginationData,
    }),
    queryFn: () =>
      getClassesByOrgIdWithPaginationApi(
        {
          userId: userId!,
          orgId: orgId!,
          searchValue,
        },
        paginationData
      ),
    enabled: Boolean(userId && orgId),
    ...customQueryOptions,
  });
}

export { type Class };
